import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import dialogTypes from 'src/js/modules/dialogs/constants/dialog-types';
import {
  i18next,
  site,
  siteLocales,
  dialogs,
  saving,
  template,
  hooks,
  integration,
  readOnly,
  errors,
  view,
} from '@yola/ws-sdk';
import withFeatureFlags from 'yola-editor/src/js/modules/feature-flags/hoc/with-feature-flags';
import segment from 'src/js/modules/analytics/segment';
import websiteSettings from 'src/js/modules/website-settings';
import saveSite, { saveSiteWithEffect } from 'src/js/modules/saving/helpers/save-site';
import { verifiers, selectors } from '@yola/subscription-manager-js';
import getFeatureLimits from 'yola-editor/src/js/modules/features/accessors/get-feature-limits';
import isDeveloperMode from 'yola-editor/src/js/modules/user/accessors/is-developer-mode';
import PageManager from '../components/page-manager';
import getOptionsToCreatePage from '../helpers/get-options-to-create-page';
import generatePageName from '../helpers/generate-page-title';
import getNewPageModalCaptions from '../helpers/get-new-page-modal-captions';
import getUpgradeBannerVisibilityItems from '../helpers/get-upgrade-banner-visibility-items';
import buildPageTreeItems from '../helpers/build-page-tree-items';
import constants from '../constants';
import isPageCreationAvailable from '../verifiers/is-page-creation-available';
import injectStoreId from '../../onlinestore/helpers/inject-store-id';
import fieldNames from '../constants/field-names';
import pageTypes from '../constants/page-types';
import insertAllBlocksToThePage from '../helpers/insert-all-blocks-to-the-page';
import ai from '../../ai';
import settingTypes from '../../website-settings/constants/setting-types';
import reloadSiteMeta from '../../ai-wizard/helpers/reload-site-meta';
import triggerIds from '../../analytics/segment/constants/trigger-ids';

const {
  trackers: {
    trackEvent,
    trackPageManagerSearchPerformed,
    trackPageManagerSearchClicked,
    trackAddAiPageTriggerClicked,
  },
  constants: { events },
} = segment;

const { helpers } = websiteSettings;

const {
  constants: { errorTypes, sourceIds },
  helpers: { initiatePageReplacement },
} = ai;

function createFilterCb(pages, locale) {
  const pageManagerOptions = [pageTypes.ABOUT_US, pageTypes.CONTACT_US];
  const targetPages = pages.filter((page) => page.locale === locale);

  return (option) =>
    pageManagerOptions.includes(option.id) &&
    !targetPages.some((page) => page.recipeId === option.id);
}

const getCaptions = () => ({
  searchPlaceholder: `${i18next.t('Search')}...`,
  addPage: i18next.t('Add page'),
  cancel: i18next.t('Cancel'),
  select: i18next.t('Select'),
  batchDelete: i18next.t('Delete'),
  setAsHomePage: i18next.t('Use as Homepage'),
  fullscreenTooltip: i18next.t('Full screen'),
  title: i18next.t('Page manager'),
  searchEmptyTitle: i18next.t('Sorry!'),
  searchEmptyText: i18next.t('Nothing was found'),
  pageTemplatesSection: i18next.t('Generate pages with AI'),
  hoveredTemplateControl: i18next.t('Generate'),
  createFolder: i18next.t('Create folder'),
  addNewPage: i18next.t('Add a new page'),
  pages: i18next.t('Your pages'),
  selected: i18next.t('selected'),
  noItemsSelectedTooltip: i18next.t('Select pages to continue'),
});

function PageManagerContainer(props) {
  const {
    pages,
    locale,
    onClose,
    activePageId,
    pageTemplates,
    featureFlags,
    currentPackage,
    upsells,
    currentPackageType,
    onSetActivePage,
    savingStatus,
    onUnsavedActivePageChange,
    html,
    htmlCheckpoint,
    onCopyPage,
    onAddNewPageClick,
    onAddNewPage,
    onPageSettingsClick,
    onDeletePageClick,
    onSetHomePageClick,
    onDeveloperConsoleClick,
    onGeneratePageCopy,
    openPageCreation,
    onCreateFolderClick,
    isReadOnlyModeEnabled,
    siteSettings,
    businessCategory,
  } = props;
  const captions = getCaptions();
  const {
    developer_console: isDeveloperConsoleEnabled,
    ai_page_wizard: aiPageWizardEnabled,
    ai_generate_page_copy: aiGeneratePageCopyEnabled,
  } = featureFlags;

  const [filteredPages, setFilteredPages] = useState([]);
  const [filtered, setFiltered] = useState(false);
  const [selectedItemsMap, setSelectedItemsMap] = useState({});

  const optionsToCreatePage = getOptionsToCreatePage(createFilterCb(pages, locale));

  function onNestedDialogSubmit(callback, ...data) {
    const { UNSAVED } = saving.constants.statuses;

    if (savingStatus === UNSAVED && html !== htmlCheckpoint) {
      onUnsavedActivePageChange(() => callback(...data));
    } else {
      callback(...data);
    }
  }

  function onItemClick(item) {
    const { UNSAVED } = saving.constants.statuses;

    // Do nothing if user clicks on active page
    if (item.id === activePageId) {
      return;
    }

    if (savingStatus === UNSAVED && html !== htmlCheckpoint) {
      onUnsavedActivePageChange(onSetActivePage.bind(null, item.id));
    } else {
      onSetActivePage(item.id);
    }
  }

  function handleCopyPage(e, targetPageId) {
    e.stopPropagation();
    const isCopiedPage = true;
    onAddNewPageClick({
      pageTitle: generatePageName(pages, targetPageId),
      captions: getNewPageModalCaptions(isCopiedPage),
      isDisplayInNavAvailable: template.verifiers.isVariableGlobalsAvailable('navigation'),
      onSubmit: (values) => onNestedDialogSubmit(onCopyPage, targetPageId, locale, values),
    });
  }

  function handleTranslatePages({ pageIds, bulkEditing = false } = {}) {
    if (isReadOnlyModeEnabled) {
      errors.operations.catchError(null, errors.types.READ_ONLY_ERROR);
      return;
    }

    dialogs.operations.show(dialogTypes.TRANSLATE_PAGE, {
      initialValues: {
        pageIds,
      },
      bulkEditing,
    });
  }

  function handleAddNewPage() {
    const { ai_page_wizard: isAiPageWizardEnabled } = featureFlags;
    const isGenerateWithAiAvailable = isAiPageWizardEnabled;
    const isCopiedPage = false;

    onAddNewPageClick({
      pageTitle: generatePageName(pages),
      pageTemplate: constants.BLANK_PAGE_TEMPLATE_ID,
      captions: getNewPageModalCaptions(isCopiedPage, isGenerateWithAiAvailable),
      isDisplayInNavAvailable: template.verifiers.isVariableGlobalsAvailable('navigation'),
      isGenerateWithAiAvailable,
      onSubmit: (values) => onNestedDialogSubmit(onAddNewPage, values),
    });
  }

  function handleGeneratePageCopy(pageId) {
    const shouldShowAiOnboarding =
      !siteSettings[settingTypes.BUSINESS_NAME] || !siteSettings[settingTypes.BUSINESS_DESCRIPTION];

    trackEvent(events.AI_GENERATE_PAGE_COPY_CLICKED);

    const generatePageCopy = () => onGeneratePageCopy(pageId, locale);
    const showLoadingDialog = () =>
      dialogs.operations.show(dialogTypes.UPLOAD_PROGRESS_DIALOG, {
        overlay: 'visible',
        cancelable: false,
        progressType: 'indeterminate',
        width: 336,
        height: 109,
        minHeight: 109,
        captions: {
          title: `${i18next.t('Generating page copy')}...`,
        },
      });

    saveSiteWithEffect(() => {
      if (shouldShowAiOnboarding) {
        dialogs.operations.show(dialogTypes.AI_ONBOARDING_DIALOG, {
          sourceId: sourceIds.GENERATE_PAGE_COPY,
          title: i18next.t('Generate page copy'),
          onBeforeSave: showLoadingDialog,
          onDialogMainAction: generatePageCopy,
        });

        return;
      }

      showLoadingDialog();
      generatePageCopy();
    });
  }

  function handleSetHomePage(e, item) {
    e.stopPropagation();
    onSetHomePageClick(item.id, locale);
  }

  function handleOpenPageCreation(pageTypeItem) {
    const { id = pageTypes.EMPTY_PAGE } = pageTypeItem || {};

    if (id !== pageTypes.EMPTY_PAGE) {
      trackAddAiPageTriggerClicked({
        recipeId: id,
        businessCategory,
      });
    }

    const triggerId =
      id === pageTypes.EMPTY_PAGE ? triggerIds.ADD_PAGE_PLUS_BUTTON : triggerIds.AI_PAGE;

    openPageCreation({
      pageType: id,
      onAddPageFromTemplate: onAddNewPage,
      triggerId,
    });
  }

  function handleEditPageSetting(e, item) {
    e.stopPropagation();
    onPageSettingsClick(item.id);
  }

  function handleDeletePage(e, item) {
    e.stopPropagation();
    onDeletePageClick(item.id, item.title);
  }

  function handleOpenDeveloperConsole(event, item) {
    event.stopPropagation();
    onDeveloperConsoleClick(item);
  }

  function handleAddNewPageClick() {
    if (aiPageWizardEnabled) {
      handleOpenPageCreation();
    } else {
      handleAddNewPage();
    }

    trackEvent(events.ADD_PAGE_TRIGGER_CLICKED, { triggerId: triggerIds.ADD_PAGE_PLUS_BUTTON });
  }

  function handleCreateFolderClick() {
    trackEvent(events.ADD_FOLDER_TRIGGER_CLICKED, {
      triggerId: triggerIds.ADD_FOLDER_PLUS_BUTTON,
    });
    onCreateFolderClick(locale);
  }

  function filter(value) {
    if (!value) {
      setFiltered(false);
      return;
    }

    const inputText = value.trim().toLowerCase();
    const filteredItems = pages.filter((item) => item.name.toLowerCase().includes(inputText));

    trackPageManagerSearchPerformed({ searchTerm: value, searchResults: filteredItems.length });

    if (value) {
      setFiltered(true);
      setFilteredPages(filteredItems);
    } else {
      setFiltered(false);
    }
  }

  const pagesFeatureLimit = getFeatureLimits(currentPackage).pagesCount;

  const isFreePackageType = verifiers.isFreeHostingPackageType(currentPackageType);

  const upgradeBannerVisibilityItems = getUpgradeBannerVisibilityItems({
    isFreePackageType,
    pagesFeatureLimit,
    pagesAmount: pages.length,
  });

  const filteredItems = (filtered ? filteredPages : pages).filter((item) => item.locale === locale);

  const items = buildPageTreeItems(filteredItems, {
    isHomePage: (item) => site.verifiers.isHomePage(item.path, helpers.getLanguageCodes()),
    activePageId,
    isDeveloperConsoleEnabled: Boolean(isDeveloperConsoleEnabled || isDeveloperMode()),
    aiGeneratePageCopyEnabled: Boolean(aiGeneratePageCopyEnabled),
    onSetHomePageClick: handleSetHomePage,
    onCopyPageClick: handleCopyPage,
    onPageSettingsClick: handleEditPageSetting,
    onTranslatePageClick: (e, itemId) => {
      e.stopPropagation();
      handleTranslatePages({ pageIds: [itemId] });
    },
    onGeneratePageCopyClick: handleGeneratePageCopy,
    onDeveloperConsoleClick: handleOpenDeveloperConsole,
    onDeletePageClick: handleDeletePage,
  });

  const onItemSelect = (itemId) => {
    setSelectedItemsMap((prevState) => ({ ...prevState, [itemId]: !prevState[itemId] }));
  };

  const isAllItemsSelected = useMemo(
    () => !Object.values(selectedItemsMap).some((value) => !value),
    [selectedItemsMap]
  );

  const onAllItemsSelect = useCallback(() => {
    setSelectedItemsMap((prevState) =>
      Object.keys(prevState).reduce((acc, key) => {
        acc[key] = !isAllItemsSelected;
        return acc;
      }, {})
    );
  }, [isAllItemsSelected]);

  const onCancelSelection = () => {
    setSelectedItemsMap((prevState) =>
      Object.keys(prevState).reduce((acc, key) => {
        acc[key] = false;
        return acc;
      }, {})
    );

    trackEvent(events.PAGE_MANAGER_BULK_EDITING_CANCELLED);
  };

  const bulkPagesActions = [
    {
      id: 'translatePage',
      iconGlyph: 'translate',
      title: `${i18next.t('Translate to')}...`,
      onClick: () => {
        const pageIds = Object.keys(selectedItemsMap).filter((key) => selectedItemsMap[key]);
        handleTranslatePages({ pageIds, bulkEditing: true });
      },
    },
  ];

  useEffect(() => {
    const itemsCheckboxValues = items.reduce((acc, item) => {
      acc[item.id] = false;
      return acc;
    }, {});

    setSelectedItemsMap(itemsCheckboxValues);

    // eslint-disable-next-line yola/react-hooks/exhaustive-deps
  }, []);

  return (
    <PageManager
      items={items}
      isPageCreationAvailable={isPageCreationAvailable(pageTemplates)}
      onItemClick={onItemClick}
      onClose={onClose}
      onAddNewPage={handleAddNewPageClick}
      onCreateFolder={handleCreateFolderClick}
      onSearchClick={trackPageManagerSearchClicked}
      filter={filter}
      captions={captions}
      optionsToCreatePage={aiPageWizardEnabled ? optionsToCreatePage : null}
      onTemplateItemClick={handleOpenPageCreation}
      bulkPagesActions={bulkPagesActions}
      isAllItemsSelected={isAllItemsSelected}
      selectedItemsMap={selectedItemsMap}
      onItemSelect={onItemSelect}
      onAllItemsSelect={onAllItemsSelect}
      onCancelSelection={onCancelSelection}
      upgradeBanner={{
        ...upgradeBannerVisibilityItems,
        title: `${i18next.t('Pages')}:`,
        buttonText: i18next.t('Get more'),
        current: pages.length,
        total: pagesFeatureLimit.amount,
        onUpgrade: () => {
          upsells.pagesCount.onUpgrade().catch(() => {
            console.error('Upsell cancelled');
          });
        },
      }}
    />
  );
}

PageManagerContainer.propTypes = {
  pages: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string,
      id: PropTypes.string,
    })
  ).isRequired,
  locale: PropTypes.string.isRequired,
  onDeletePageClick: PropTypes.func.isRequired,
  onPageSettingsClick: PropTypes.func.isRequired,
  onSetHomePageClick: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  onSetActivePage: PropTypes.func.isRequired,
  onAddNewPage: PropTypes.func.isRequired,
  onCopyPage: PropTypes.func.isRequired,
  onAddNewPageClick: PropTypes.func.isRequired,
  onDeveloperConsoleClick: PropTypes.func.isRequired,
  onGeneratePageCopy: PropTypes.func.isRequired,
  openPageCreation: PropTypes.func.isRequired,
  onCreateFolderClick: PropTypes.func.isRequired,
  activePageId: PropTypes.string.isRequired,
  currentPackageType: PropTypes.string.isRequired,
  pageTemplates: PropTypes.arrayOf(PropTypes.object).isRequired,
  savingStatus: PropTypes.string.isRequired,
  onUnsavedActivePageChange: PropTypes.func.isRequired,
  html: PropTypes.string.isRequired,
  htmlCheckpoint: PropTypes.string.isRequired,
  featureFlags: PropTypes.shape({
    developer_console: PropTypes.bool,
    ai_page_wizard: PropTypes.bool,
    ai_localization: PropTypes.bool,
    ai_generate_page_copy: PropTypes.bool,
  }).isRequired,
  upsells: PropTypes.shape({
    pagesCount: PropTypes.shape({
      onUpgrade: PropTypes.func,
    }),
  }).isRequired,
  currentPackage: PropTypes.shape().isRequired,
  businessCategory: PropTypes.oneOfType([PropTypes.string, PropTypes.oneOf([null])]).isRequired,
  // eslint-disable-next-line yola/react/no-unused-prop-types
  onDialogCancel: PropTypes.func.isRequired,
  isReadOnlyModeEnabled: PropTypes.bool.isRequired,
  siteSettings: PropTypes.shape({
    [settingTypes.BUSINESS_NAME]: PropTypes.string,
    [settingTypes.BUSINESS_DESCRIPTION]: PropTypes.string,
  }).isRequired,
};

const mapStateToProps = (state) => {
  const pageTemplates = template.selectors.getPageTemplates(state);
  const activePageId = site.selectors.getActivePageId(state);
  const activePage = site.selectors.getPage(state, activePageId);
  const htmlCheckpoint = activePage && activePage.htmlCheckpoint;
  const html = activePage && activePage.html;
  const locale = siteLocales.selectors.getCurrentLocale(state);
  const currentPackage = selectors.getCurrentPackage(state);
  const upsells = integration.selectors.getUpsells(state);
  const isReadOnlyModeEnabled = readOnly.selectors.getReadOnlyStatus(state);
  const siteSettings = site.selectors.getSettings(state);
  const currentPackageType = selectors.getCurrentPackageType(state);

  return {
    upsells,
    currentPackage,
    pageTemplates,
    activePageId,
    htmlCheckpoint,
    html,
    locale,
    pages: site.selectors.getPages(state),
    businessCategory: site.selectors.getBusinessCategory(state),
    savingStatus: saving.selectors.getStatus(state),
    isReadOnlyModeEnabled,
    siteSettings,
    currentPackageType,
  };
};

const mapDispatchToProps = (dispatch, { featureFlags, onDialogCancel }) => {
  const actions = {
    onDeletePageClick(pageId, pageTitle) {
      dispatch(dialogs.actions.show(dialogTypes.DELETE_PAGE, { pageId, pageTitle }));
    },

    onPageSettingsClick(pageId) {
      dispatch(dialogs.actions.show(dialogTypes.PAGE_SETTINGS, { pageId }));
    },

    onSetHomePageClick(pageId, locale) {
      dispatch(
        site.actions.setHomePage(
          pageId,
          locale,
          site.verifiers.isMultilingualSite(),
          helpers.getLanguageCodes()
        )
      );
      saveSite();
    },

    onClose() {
      onDialogCancel();
      dispatch(dialogs.actions.hide());
    },

    onSetActivePage(pageId) {
      dispatch(site.actions.setActivePage(pageId));
    },

    onUnsavedActivePageChange(callback) {
      dispatch(
        dialogs.actions.show(dialogTypes.UNSAVED_WARNING, {
          callback,
          closingCallback: () => dispatch(dialogs.actions.show(dialogTypes.PAGE_MANAGER)),
        })
      );
    },

    onAddNewPageClick(options) {
      dispatch(dialogs.actions.show(dialogTypes.NEW_PAGE_DIALOG, { ...options }));
    },

    openPageCreation(options) {
      dispatch(dialogs.actions.show(dialogTypes.ADD_PAGE_DIALOG, { ...options }));
    },

    onCreateFolderClick(locale) {
      const onCreateFolder = (folderName) => {
        dispatch(site.actions.addFolder({ folderName, locale }));
        dispatch(dialogs.actions.show(dialogTypes.PAGE_MANAGER));
      };

      dispatch(
        dialogs.actions.show(dialogTypes.CREATE_FOLDER_DIALOG, {
          onSubmit: onCreateFolder,
          triggerId: triggerIds.ADD_FOLDER_PLUS_BUTTON,
        })
      );
    },

    async onAddNewPage(values) {
      const { business_info_in_website_settings: isBusinessInfoInWebsiteSettingsEnabled } =
        featureFlags;
      const {
        [fieldNames.PAGE_NAME_INPUT]: pageTitle,
        [fieldNames.PAGE_TEMPLATE_SELECT]: selectedPageTemplate,
        [fieldNames.DISPLAY_IN_NAVIGATION_CHECKBOX]: displayInNavigation,
      } = values;
      const pageModifiers = [];

      if (selectedPageTemplate === constants.SHOP_PAGE_TEMPLATE_ID) {
        pageModifiers.push(injectStoreId);
      }

      if (selectedPageTemplate === pageTypes.ALL_BLOCKS) {
        pageModifiers.push(insertAllBlocksToThePage(featureFlags), injectStoreId);
      }

      const pageModifier = (documentPage) => {
        pageModifiers.forEach((fn) => fn(documentPage));
      };

      const pageTemplate =
        !selectedPageTemplate || selectedPageTemplate === pageTypes.ALL_BLOCKS
          ? constants.BLANK_PAGE_TEMPLATE_ID
          : selectedPageTemplate;

      const onAfterAddNewPage = () => {
        dispatch(dialogs.actions.show(dialogTypes.PAGE_MANAGER));

        if (selectedPageTemplate === pageTypes.ALL_BLOCKS) {
          setTimeout(() => {
            // This is a workaround to reload all previously added blocks
            dispatch(view.actions.forceReloadView());
          }, 0);
        }
      };

      hooks.operations.subscribe(hooks.availableHooks.ON_SITE_LOADED, onAfterAddNewPage, {
        once: true,
      });

      const displayBusinessName = isBusinessInfoInWebsiteSettingsEnabled ? true : null;

      try {
        await site.operations.createPageFromTemplate({
          pageTemplateId: pageTemplate,
          title: pageTitle,
          displayInNavigation,
          pageModifier,
          displayBusinessName,
        });
      } catch (e) {
        hooks.operations.unsubscribe(hooks.availableHooks.ON_SITE_LOADED, onAfterAddNewPage);
      }
    },

    onCopyPage(targetPageId, locale, values) {
      const {
        [fieldNames.PAGE_NAME_INPUT]: pageTitle,
        [fieldNames.DISPLAY_IN_NAVIGATION_CHECKBOX]: displayInNavigation,
      } = values;

      dispatch(
        site.actions.copyPage(
          targetPageId,
          locale,
          pageTitle,
          displayInNavigation,
          site.verifiers.isMultilingualSite()
        )
      );

      hooks.operations.subscribe(hooks.availableHooks.ON_BEFORE_PAGE_LOADED, saveSite, {
        once: true,
      });

      dispatch(dialogs.actions.show(dialogTypes.PAGE_MANAGER));
    },

    async onGeneratePageCopy(pageId, locale) {
      try {
        await initiatePageReplacement({
          pageId,
          locale,
          enforceTextRegeneration: true,
        });
        await reloadSiteMeta();

        dispatch(site.actions.setActivePage(pageId));

        const onSiteLoaded = () => {
          saveSiteWithEffect(() => {
            dispatch(dialogs.actions.hide());
          });
        };

        hooks.operations.subscribe(hooks.availableHooks.ON_SITE_LOADED, onSiteLoaded, {
          once: true,
        });
      } catch (e) {
        const errorType = e.results?.code || errorTypes.UNKNOWN_REPLACE_PAGE;

        dispatch(
          dialogs.actions.show(dialogTypes.AI_ERROR_DIALOG, {
            errorType,
            onRenew: () => {
              dispatch(dialogs.actions.hide());
            },
          })
        );
      }
    },

    onDeveloperConsoleClick(page) {
      dispatch(dialogs.actions.show(dialogTypes.DEVELOPER_CONSOLE, { page }));
    },
  };

  return actions;
};

export default withFeatureFlags([
  'developer_console',
  'ai_page_wizard',
  'business_info_in_website_settings',
  'hidden_blocks',
  'contact_forms_disabled',
  'ai_localization',
  'ai_generate_page_copy',
])(connect(mapStateToProps, mapDispatchToProps)(PageManagerContainer));
